import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}
type Confirm = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm>(async () => false);

/**
 * App-level confirmation dialogs. `const confirm = useConfirm()` then
 * `if (await confirm({ title, message, danger: true })) { …do it… }` — a styled,
 * RTL, keyboard-accessible replacement for window.confirm, so destructive actions
 * get a real, focus-managed prompt instead of a native browser dialog.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<
    (ConfirmOptions & { resolve: (v: boolean) => void }) | null
  >(null);

  const confirm = useCallback<Confirm>(
    (opts) => new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    [],
  );
  const settle = (v: boolean) => {
    pending?.resolve(v);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <Modal
          title={pending.title ?? 'تأیید'}
          onClose={() => settle(false)}
          actions={
            <>
              <Button variant={pending.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
                {pending.confirmLabel ?? 'تأیید'}
              </Button>
              <Button variant="secondary" onClick={() => settle(false)}>
                {pending.cancelLabel ?? 'انصراف'}
              </Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{pending.message}</p>
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  return useContext(ConfirmContext);
}
